/**
 * @fileoverview Profil du compte agent connecté.
 *
 * Page frontend-only : les champs décrivent le futur parcours de mise à jour
 * du profil sans appeler d'endpoint. Les modifications s'ouvrent dans un sheet
 * pour garder la page principale lisible.
 */

import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  PageHeader,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@gnpc/ui';
import { Building2, Mail, Phone, Save, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABELS } from '../data/backoffice.ts';
import { AccountNav } from '../features/account/AccountNav.tsx';
import { useCurrentUser } from '../features/auth/CapabilityProvider.tsx';

export function AccountProfilePage(): JSX.Element {
  const { user } = useCurrentUser();

  return (
    <Sheet>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Compte agent"
          title="Profil"
          description="Consulter les informations du compte connecté et préparer les demandes de mise à jour."
          actions={
            <SheetTrigger asChild>
              <Button>
                <Save className="size-4" />
                Modifier le profil
              </Button>
            </SheetTrigger>
          }
        />

        <AccountNav />

        <section className="grid gap-4">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Identité agent</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5">
              <div className="flex flex-wrap items-center gap-4">
                <Avatar className="size-14">
                  <AvatarFallback className="bg-quipux-navy-100 text-quipux-navy-950 font-semibold">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-quipux-navy-950 text-lg font-semibold">{user.fullName}</p>
                  <p className="text-muted-foreground text-sm">{user.email}</p>
                </div>
              </div>

              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-lg border border-border p-4">
                  <dt className="text-muted-foreground">Rôle actuel</dt>
                  <dd className="mt-1 flex items-center gap-2 font-medium">
                    <ShieldCheck className="text-quipux-blue-700 size-4" />
                    {ROLE_LABELS[user.role]}
                  </dd>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <dt className="text-muted-foreground">Statut</dt>
                  <dd className="mt-1">
                    <Badge>Actif</Badge>
                  </dd>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <dt className="text-muted-foreground">Téléphone professionnel</dt>
                  <dd className="mt-1 font-medium">+225 07 XX XX XX 42</dd>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <dt className="text-muted-foreground">Service</dt>
                  <dd className="mt-1 font-medium">Opérations GNPC</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </section>
      </div>

      <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
        <SheetHeader className="border-border border-b p-5 pr-12">
          <SheetTitle>Modifier le profil</SheetTitle>
          <SheetDescription>
            Toute mise à jour du profil passe par une validation du service d'administration avant
            prise en compte.
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 p-5">
          <FormField label="Nom complet" required>
            <Input leftIcon={<UserRound />} defaultValue={user.fullName} />
          </FormField>
          <FormField label="Email professionnel" required>
            <Input leftIcon={<Mail />} defaultValue={user.email} readOnly />
          </FormField>
          <FormField label="Téléphone professionnel">
            <Input leftIcon={<Phone />} defaultValue="+225 07 00 00 00 42" />
          </FormField>
          <FormField label="Service">
            <Input leftIcon={<Building2 />} defaultValue="Opérations GNPC" />
          </FormField>
        </div>
        <SheetFooter className="border-border border-t p-5">
          <SheetClose asChild>
            <Button
              onClick={() =>
                toast.success('Profil mis à jour', {
                  description:
                    "La demande de mise à jour a été transmise au service d'administration.",
                })
              }
            >
              <Save className="size-4" />
              Soumettre pour validation
            </Button>
          </SheetClose>
          <SheetClose asChild>
            <Button variant="outline">Annuler</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
